import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, 
  MapPin, 
  CheckCircle2,
  Award,
  Monitor,
  Users,
  Clock,
  AlertCircle,
  Baby,
  Sparkles
} from 'lucide-react';

/**
 * Yoga & Wellness Service Cards
 * Based on wireframe specifications with certification compliance
 */

// Card A: Anchor Card - Yoga Classes
export const YogaClassCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  hasOnlineOption = true,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-purple-200 bg-purple-50/20'
      }`}
      data-testid="yoga-class-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Wellness Service</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧘</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Yoga Classes</h3>
            <p className="text-xs text-gray-500">Beginner · Flow · Gentle</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Group &amp; private options</span>
          </p>
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Online or local studio</span>
          </p>
          <p className="text-xs text-purple-700 flex items-center gap-1">
            <Award className="h-3 w-3 flex-shrink-0" />
            <span>Certified instructor</span>
          </p>
        </div>

        {/* Rating & Location */}
        <div className="flex items-center justify-between text-xs mb-2">
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
            <span className="text-gray-500">({provider?.review_count || 320} sessions)</span>
          </div>
          <span className="text-gray-500 flex items-center gap-1">
            {hasOnlineOption && <Monitor className="h-3 w-3" />}
            <span>{hasOnlineOption ? 'Online' : ''}{hasOnlineOption && provider?.travel_radius_km ? ' / ' : ''}{provider?.travel_radius_km ? `${provider.travel_radius_km}km` : ''}</span>
          </span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 10}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-purple-600 hover:bg-purple-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-yoga-class"
        >
          Book class
        </Button>
      </CardContent>
    </Card>
  );
};

// Card B: Meditation & Mindfulness
export const MeditationCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-indigo-200 bg-indigo-50/20'
      }`}
      data-testid="meditation-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Mindfulness</span>
          </div>
        )}

        {/* High Demand Badge */}
        <Badge className="bg-indigo-100 text-indigo-700 text-xs mb-2">
          <Sparkles className="h-3 w-3 mr-1" />
          High Demand
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🧠</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Meditation &amp; Mindfulness</h3>
            <p className="text-xs text-gray-500">Stress · Calm · Focus</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-indigo-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Guided sessions</span>
          </p>
          <p className="text-xs text-indigo-700 flex items-center gap-1">
            <Monitor className="h-3 w-3 flex-shrink-0" />
            <span>Online available</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
          <span className="text-gray-500">({provider?.review_count || 410} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 8}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-indigo-600 hover:bg-indigo-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="join-meditation"
        >
          Join session
        </Button>
      </CardContent>
    </Card>
  );
};

// Card C: Mobility & Back-Care Yoga
export const MobilityYogaCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-green-200 bg-green-50/20'
      }`}
      data-testid="mobility-yoga-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Mobility</span>
          </div>
        )}

        {/* Problem-Specific Badge */}
        <Badge className="bg-green-100 text-green-700 text-xs mb-2">
          Desk Workers
        </Badge>

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🌿</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Mobility &amp; Back-Care Yoga</h3>
            <p className="text-xs text-gray-500">Desk pain · Flexibility</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Gentle &amp; safe</span>
          </p>
          <p className="text-xs text-green-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Ideal for beginners</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.8'}</span>
          <span className="text-gray-500">({provider?.review_count || 210} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 12}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-green-600 hover:bg-green-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-mobility-yoga"
        >
          Book session
        </Button>
      </CardContent>
    </Card>
  );
};

// Card D: Breathwork & Relaxation
export const BreathworkCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border hover:shadow-md transition-all ${
        isSponsored ? 'border-blue-300 bg-blue-50/30' : 'border-teal-200 bg-teal-50/20'
      }`}
      data-testid="breathwork-card"
    >
      <CardContent className="p-3">
        {/* Sponsored Badge */}
        {isSponsored && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Relaxation</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">🌬️</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Breathwork &amp; Relaxation</h3>
            <p className="text-xs text-gray-500">Stress relief · Sleep</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 flex-shrink-0" />
            <span>Nervous system calming</span>
          </p>
          <p className="text-xs text-teal-700 flex items-center gap-1">
            <Monitor className="h-3 w-3 flex-shrink-0" />
            <span>Online sessions</span>
          </p>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1 text-xs mb-2">
          <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
          <span className="font-medium">{provider?.rating?.toFixed(1) || '4.7'}</span>
          <span className="text-gray-500">({provider?.review_count || 160} sessions)</span>
        </div>

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 10}</span>
        </p>

        {/* CTA */}
        <Button 
          className="w-full h-8 text-sm bg-teal-600 hover:bg-teal-700"
          onClick={() => onBook?.(service, provider)}
          data-testid="book-breathwork"
        >
          Book session
        </Button>
      </CardContent>
    </Card>
  );
};

// Card E: Prenatal / Specialised Yoga (Strictly Gated)
export const PrenatalYogaCard = ({
  service,
  provider,
  onBook,
  isSponsored = false,
  instructorCertified = false,
}) => {
  return (
    <Card 
      className={`overflow-hidden border-2 hover:shadow-md transition-all ${
        instructorCertified 
          ? 'border-pink-300 bg-pink-50/20' 
          : 'border-gray-200 bg-gray-50'
      }`}
      data-testid="prenatal-yoga-card"
    >
      <CardContent className="p-3">
        {/* Sponsored / Certified Badge */}
        {isSponsored && instructorCertified && (
          <div className="flex items-center gap-1 text-blue-600 text-xs mb-2">
            <span>🔹</span>
            <span>Sponsored · Certified Only</span>
          </div>
        )}

        {/* Certification Warning */}
        {!instructorCertified && (
          <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-2">
            <AlertCircle className="h-3 w-3" />
            <span>Requires certified instructor</span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-2">
          <span className="text-2xl">👶</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">Prenatal Yoga</h3>
            <p className="text-xs text-gray-500">Safe · Gentle · Guided</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-2">
          <p className={`text-xs flex items-center gap-1 ${instructorCertified ? 'text-pink-700' : 'text-gray-400'}`}>
            <Award className="h-3 w-3 flex-shrink-0" />
            <span>Pregnancy-certified instructor</span>
            {instructorCertified && <CheckCircle2 className="h-3 w-3 text-green-500" />}
          </p>
        </div>

        {/* Rating (only show if certified) */}
        {instructorCertified && (
          <div className="flex items-center gap-1 text-xs mb-2">
            <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
            <span className="font-medium">{provider?.rating?.toFixed(1) || '4.9'}</span>
            <span className="text-gray-500">({provider?.review_count || 98} sessions)</span>
          </div>
        )}

        {/* Price */}
        <p className="text-xs mb-2">
          From <span className="font-bold text-base">{provider?.currency || '£'}{service?.price || 15}</span>
        </p>

        {/* CTA */}
        <Button 
          className={`w-full h-8 text-sm ${instructorCertified ? 'bg-pink-600 hover:bg-pink-700' : 'bg-gray-400'}`}
          onClick={() => instructorCertified && onBook?.(service, provider)}
          disabled={!instructorCertified}
          data-testid="book-prenatal-yoga"
        >
          {instructorCertified ? 'Book safely' : 'Requires certification'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Subscription/Pack Card
export const WellnessPackCard = ({
  pack,
  currency = '£',
  onSubscribe,
  isPopular = false,
}) => {
  const getPackStyle = () => {
    switch (pack.id) {
      case 'weekly_pass':
        return { border: 'border-purple-300', bg: 'from-purple-50', accent: 'bg-purple-600', icon: '🧘' };
      case 'monthly_wellness':
        return { border: 'border-green-300', bg: 'from-green-50', accent: 'bg-green-600', icon: '🌿' };
      case 'beginner_starter':
        return { border: 'border-indigo-300', bg: 'from-indigo-50', accent: 'bg-indigo-600', icon: '🧘' };
      case 'stress_relief':
        return { border: 'border-teal-300', bg: 'from-teal-50', accent: 'bg-teal-600', icon: '🌬️' };
      default:
        return { border: 'border-purple-300', bg: 'from-purple-50', accent: 'bg-purple-600', icon: '🧘' };
    }
  };

  const style = getPackStyle();

  return (
    <Card 
      className={`overflow-hidden border-2 ${style.border} bg-gradient-to-br ${style.bg} to-white hover:shadow-lg transition-all`}
      data-testid={`wellness-pack-card-${pack.id}`}
    >
      <CardContent className="p-4">
        {/* Popular Badge */}
        {isPopular && (
          <Badge className="bg-yellow-500 text-white text-xs mb-2">
            ⭐ Most Popular
          </Badge>
        )}

        {/* Savings Badge */}
        {pack.savings && (
          <Badge className={`${style.accent} text-white text-xs mb-2`}>
            Save {currency}{pack.savings}
          </Badge>
        )}

        {/* Header */}
        <div className="flex items-start gap-2 mb-3">
          <span className="text-2xl">{style.icon}</span>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-tight">{pack.name}</h3>
          </div>
        </div>

        {/* Included Items */}
        <div className="space-y-1 mb-3">
          {pack.includes?.map((item, idx) => (
            <p key={idx} className="text-xs text-gray-700 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 flex-shrink-0 text-green-600" />
              <span>{item}</span>
            </p>
          ))}
        </div>

        {/* Pricing */}
        <div className="mb-3">
          <span className="font-bold text-lg">
            {currency}{pack.price}
          </span>
          {pack.period && (
            <span className="text-gray-500 text-sm"> / {pack.period}</span>
          )}
          {pack.one_time && (
            <span className="text-gray-500 text-sm"> one-time</span>
          )}
        </div>

        {/* CTA */}
        <Button 
          className={`w-full ${style.accent} hover:opacity-90`}
          onClick={() => onSubscribe?.(pack)}
          data-testid={`subscribe-${pack.id}`}
        >
          {pack.cta || 'Subscribe'}
        </Button>
      </CardContent>
    </Card>
  );
};

// Mobile Sticky Pill for Yoga & Wellness
export const YogaMobilePill = ({
  startingPrice = 8,
  currency = '£',
  onTap,
}) => {
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 lg:hidden"
      data-testid="yoga-mobile-pill"
    >
      <button
        onClick={onTap}
        className="flex items-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all active:scale-95"
      >
        <span>🧘</span>
        <span className="text-sm font-medium">Yoga &amp; Wellness nearby</span>
        <span className="text-sm">From {currency}{startingPrice}</span>
      </button>
    </div>
  );
};

// Mobile Bottom Sheet Content
export const YogaMobileSheet = ({
  services,
  providers,
  onBook,
  currency = '£',
}) => {
  const serviceTypes = [
    { type: 'yoga_class', icon: '🧘', name: 'Yoga Class', defaultPrice: 10, label: 'sessions' },
    { type: 'meditation', icon: '🧠', name: 'Meditation', defaultPrice: 8, label: 'sessions' },
    { type: 'mobility_yoga', icon: '🌿', name: 'Mobility Yoga', defaultPrice: 12, label: 'sessions' },
    { type: 'breathwork', icon: '🌬️', name: 'Breathwork', defaultPrice: 10, label: 'sessions' },
  ];

  return (
    <div className="p-4">
      <h3 className="font-bold text-lg mb-4">Yoga &amp; Wellness Services Near You</h3>
      <div className="space-y-3">
        {serviceTypes.map((st) => {
          const service = services?.find(s => s.type === st.type);
          const provider = providers?.find(p => p.services?.some(s => s.type === st.type));
          const price = service?.price || st.defaultPrice;
          const rating = provider?.rating || 4.8;

          return (
            <div 
              key={st.type}
              className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{st.icon}</span>
                <div>
                  <p className="font-medium text-sm">{st.name}</p>
                  <p className="text-xs text-gray-500">From {currency}{price}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 text-xs">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                  <span>{rating.toFixed(1)}</span>
                </div>
                <Button 
                  size="sm" 
                  className="h-8 bg-purple-600 hover:bg-purple-700"
                  onClick={() => onBook?.(service || { type: st.type, name: st.name, price }, provider)}
                >
                  Book
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Post-Checkout Upsell Card for Yoga
export const YogaPostCheckoutCard = ({
  productType,
  prompt,
  service,
  provider,
  onAdd,
  onDismiss,
}) => {
  return (
    <Card 
      className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-white"
      data-testid="yoga-post-checkout-upsell"
    >
      <CardContent className="p-3 flex items-center gap-3">
        <span className="text-2xl">🧘</span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{prompt?.title || 'Your yoga mat is on the way!'}</p>
          <p className="text-xs text-gray-500">{prompt?.subtitle || 'Want to start practising?'}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            className="text-xs h-8"
            onClick={onDismiss}
          >
            Not now
          </Button>
          <Button 
            size="sm" 
            className="text-xs h-8 bg-purple-600 hover:bg-purple-700"
            onClick={() => onAdd?.(service, provider)}
          >
            {prompt?.cta || 'Add class'} · {provider?.currency || '£'}{service?.price || 10}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Certification Level Banner
export const CertificationBanner = ({
  instructorLevel,
  requiredLevel,
  onLearnMore,
}) => {
  const levelNames = {
    0: 'No certification',
    1: 'General Yoga & Meditation',
    2: 'Mobility & Back-Care',
    3: 'Prenatal & Senior Yoga',
    4: 'Corporate Programs',
  };

  if (instructorLevel >= requiredLevel) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4" data-testid="certification-banner">
      <div className="flex items-start gap-2">
        <Award className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">
            Certification Required
          </p>
          <p className="text-xs text-amber-600 mt-1">
            This service requires Level {requiredLevel} certification ({levelNames[requiredLevel]}).
            Current level: {instructorLevel}.
          </p>
          {onLearnMore && (
            <button 
              onClick={onLearnMore}
              className="text-xs text-amber-700 underline mt-1"
            >
              Learn about certification levels
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Disclaimer Banner (Auto-shown on all yoga listings)
export const WellnessDisclaimer = () => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-4 text-xs text-gray-500" data-testid="wellness-disclaimer">
      <p>
        <strong>Disclaimer:</strong> Yoga and wellness services are for general well-being only. 
        They do not provide medical diagnosis or treatment. Consult a healthcare professional for medical concerns.
      </p>
    </div>
  );
};

export default {
  YogaClassCard,
  MeditationCard,
  MobilityYogaCard,
  BreathworkCard,
  PrenatalYogaCard,
  WellnessPackCard,
  YogaMobilePill,
  YogaMobileSheet,
  YogaPostCheckoutCard,
  CertificationBanner,
  WellnessDisclaimer,
};
